<template>
  <component
      :is="isTag"
      :href="href"
      class="button"
      :class="[type,size,{ 'fluid':isFluid,'disabled':isDisabled, 'is-uppercase':isUppercase,'with-icon':withIcon, 'icon-right':withIcon && iconPosition ==='right','icon-center':withIcon && iconPosition ==='center'}]"
      :style="styles"
      :disabled="isDisabled"
      :target="target"
      @click="clickButton($event)">
        <slot v-if="withIcon && iconPosition ==='left' ||withIcon && iconPosition ==='center' " name="left" />
        <span v-if="text" v-html="text"></span>
        <slot v-if="withIcon && iconPosition ==='right'" name="right" />
  </component>
</template>

<script setup lang="ts">
import {computed} from "vue";
import type { Props } from './model';
import type { Emits }  from './model/types';

defineOptions({
  name: 'AppButton'
})
const emit = defineEmits<Emits>();
const props = withDefaults(defineProps<Props>(),{
  type: 'primary',
  size: 's',
  withIcon: false,
  text: '',
  isUppercase: false,
  isFluid: false,
  isRounded: false,
  isDisabled:false,
  iconPosition:'',
  href: '',
  target: '',
})

const isTag = computed(() => {
  if(props.href.startsWith('#')||props.href.startsWith('/')||props.href.startsWith('http')||props.href.startsWith('mailto')){
    return 'a'
  }
  return 'button'
})
const clickButton  = (event: MouseEvent): void => {
  emit('click', event)
}

</script>
<style  lang="scss">
  .button{
      font-size: var(--font-size-20);
      line-height: var(--base-line-height);
      font-family: var(--ff-base);
      border: 0;
      outline: 0;
      display: flex;
      align-items: center;
      font-weight: 300;
      appearance: none;
      background-color: unset;
      cursor: pointer;
      -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
      >span{
        flex-shrink: 0;
      }
      color: var(--color-primary);
      &.primary, &.secondary, &.black, &.primary-ghost {
        background: var(--color-primary);
        color: var(--color-black);
        padding: 1.95rem 0;
        border-radius: 20rem;
        display: flex;
        justify-content: center;
        min-width: 30rem;
        width: max-content;
        font-size: var(--font-size-20);
        transition: background-color 0.3s, color 0.3s;
        @media screen and (max-width: 768px){
          min-width: unset;
          width: 100%;
        }
        &:hover{
          background: var(--color-primary-ghost);
        }
        &.disabled{
          background: var(--color-primary-ghost);
          pointer-events: none;
        }
      }
      &.primary-ghost{
        background: transparent;
        border: 0.1rem solid var(--color-primary);
        color:var(--color-primary);
        &:hover{
          color:var(--color-black);
          border-color: var(----color-primary-ghost);
        }
      }
      &.secondary {
        background: var(--color-secondary);
        color: var(--color-primary);
        border-radius: var(--border-radius-xs);
        &:hover {
          background: var(--color-secondary-ghost-30);
          color: var(--color-primary);
        }
        &.disable {
          opacity: .6;
          pointer-events: none;
        }
        &.s {
          @media screen and (max-width: 768px) {
            font-size: var(--font-size-14);
          }
        }
      }
      &.black {
        background: var(--color-black);
        color: var(--color-primary);
        &:hover{
          background: var(--color-secondary);
        }
        &.disable{
          background: var(--color-black-ghost);
          pointer-events: none;
        }
      }
      &.black-2{
        background: var(--color-black);
        color: var(--color-primary);
        border-radius: 0.7rem;
        &.xs {
          padding: 1rem;
          font-size: var(--font-size-16);
          @media screen and (max-width: 768px) {
            font-size: var(--font-size-14);
          }
        }
      }
      &.ghost{
        &:hover{
          opacity: 0.6;
        }
      }
      &.fluid{
        min-width: unset;
        width: 100%;
        padding-right: 0!important;
        padding-left: 0!important;
      }
      &.link{
        &.s {
          padding: 0;
          font-size: var(--font-size-16);
          @media screen and (max-width: 768px) {
            font-size: var(--font-size-14);
          }
        }
      }
      &.m {
        max-width: 24.8rem;
        font-size: var(--font-size-20);
        @media screen and (max-width: 768px) {
          max-width: 25.8rem;
          font-size: var(--font-size-16);
        }
      }
      &.s {
        padding: 1.65rem var(--space-05);
        min-width: auto;
        font-size: var(--font-size-16);
        @media screen and (max-width: 768px) {
          padding: 1.25rem 2rem;
        }
      }
      &.xs {
        padding: 1.65rem 0;
        font-size: var(--font-size-14);
        @media screen and (max-width: 768px) {
          padding: 1.25rem 0;
        }
      }
    &.icon-right span{
      padding-right: var(--space-05);
    }
    &.icon-center{
      i{
        margin-right: 0;
      }
    }
    &.is-uppercase{
      text-transform: uppercase;
      text-align: left;
    }

    &:hover {
     i.icon{
        color: var(--color-primary);
        border-color: var(--color-primary);
      }
    }
  }
</style>